import {useCallback, useEffect, useMemo, useState} from "react";
import {paymentMethodsApi} from "../Account/Billing/api";
import {cardDescriptor, cardLabel, cardNickname} from "../ui/CardLabel";
import {InlineEdit} from "../ui/InlineEdit";
import AddCC from "./CC/AddCC.js";
import {
  Box,
  Flex,
  Text,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  useToast,
  Heading,
  IconButton,
} from "@chakra-ui/react";
import {DeleteIcon} from "@chakra-ui/icons";

// The label used to be written out here with a `****` mask, while Account >
// Payment methods used `••••` for the same card. Both now read through
// components/ui/CardLabel.

export default function Payment({state, dispatch}) {
  const toast = useToast();
  const [cards, setCards] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [updating, setUpdating] = useState(false);
  const patient = state.patient;

  // Same endpoint family as Account > Payment methods. The patient id is
  // never sent — the server reads it from the verified token — and every
  // write is ownership-checked against the resolved Stripe customer.
  const auth = useMemo(() => ({jwt: state?.jwt || null}), [state?.jwt]);

  // `expectPaymentMethodId` is the card Stripe just confirmed. A card can
  // take a moment to show up in `paymentMethods.list` after the SetupIntent
  // succeeds, which left the table on "No credit cards saved" right after a
  // successful save. When we know what to expect, poll briefly for it.
  const loadCards = useCallback(async (expectPaymentMethodId) => {
    const attempts = expectPaymentMethodId ? 4 : 1;
    try {
      for (let attempt = 0; attempt < attempts; attempt++) {
        const res = await paymentMethodsApi.list(auth);
        const methods = res?.methods || [];
        setCards(methods);
        if (
          !expectPaymentMethodId ||
          methods.some((pm) => pm.id === expectPaymentMethodId)
        ) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 600));
      }
    } catch (error) {
      console.error("Failed to load payment methods:", error);
      toast({
        title: "Unable to Load Payment Methods",
        description:
          "We couldn't retrieve your saved cards. Please try refreshing the page.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  useEffect(() => {
    if (state.db) loadCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorMessage || successMessage) {
      toast({
        title: successMessage ? "Success" : "Error",
        description: successMessage || errorMessage,
        status: successMessage ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [errorMessage, successMessage]);

  const onSetDefault = async (pm) => {
    if (updating) return;
    setSuccessMessage("");
    setErrorMessage("");
    setUpdating(true);
    try {
      await paymentMethodsApi.setDefault(auth, pm.id);
      setCards((prev) =>
        prev.map((card) => ({...card, isDefault: card.id === pm.id}))
      );
      setSuccessMessage("Successfully updated default credit card.");
    } catch (e) {
      setErrorMessage(e?.message || "Could not update default.");
    } finally {
      setUpdating(false);
    }
  };

  // This screen owns its card list (no react-query here), so the renamed row is
  // patched in place with the value the SERVER stored, which is trimmed and
  // capped and may differ from what was typed. Rethrows so InlineEdit keeps the
  // draft on screen when the request fails.
  const onRename = async (pm, nickname) => {
    try {
      const res = await paymentMethodsApi.setNickname(auth, pm.id, nickname);
      setCards((prev) =>
        prev.map((card) =>
          card.id === pm.id ? {...card, nickname: res?.nickname ?? null} : card
        )
      );
    } catch (e) {
      setErrorMessage(e?.message || "Could not rename card.");
      throw e;
    }
  };

  const onDetach = async (pm) => {
    if (updating) return;
    if (!window.confirm(`Remove ${cardLabel(pm)}?`)) return;
    setSuccessMessage("");
    setErrorMessage("");
    setUpdating(true);
    try {
      await paymentMethodsApi.detach(auth, pm.id);
      setCards((prev) => prev.filter((card) => card.id !== pm.id));
      setSuccessMessage("Credit card removed successfully.");
    } catch (e) {
      setErrorMessage(e?.message || "Could not remove card.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box w="full" px={0} py={2}>
      <Box
        bg="gray.50"
        borderRadius="12px"
        px={{base: 4, md: 6}}
        py={{base: 4, md: 6}}
        mb={4}
      >
        <VStack spacing={4} alignItems="stretch">
          <Heading size="sm" color="gray.800">
            Credit Card(s)
          </Heading>
          <Box w="full" overflowX="auto">
            <Table size="sm" w="full" minW="520px">
            <Thead>
              <Tr>
                <Th textAlign="center">Card</Th>
                {/* Its own column. The rename used to sit beside the card
                    number, where a pencil reads as "edit the number" — the one
                    thing that cannot be edited on a saved card. */}
                <Th textAlign="center">Nickname</Th>
                <Th textAlign="center">Expiry Date</Th>
                <Th textAlign="center">Default</Th>
                <Th textAlign="center">Remove</Th>
              </Tr>
            </Thead>
            <Tbody>
              {cards?.length > 0 ? (
                cards.map((c) => (
                  <Tr key={c.id}>
                    <Td textAlign="center">{cardDescriptor(c)}</Td>
                    <Td textAlign="center">
                      <Flex justify="center">
                        <InlineEdit
                          value={cardNickname(c)}
                          onSave={(next) => onRename(c, next)}
                          editLabel={`Name ${cardDescriptor(c)}`}
                          placeholder="e.g. My personal Visa"
                          inputWidth="160px"
                        >
                          {/* An unnamed card says so, rather than leaving a
                              blank cell that looks like a failed load. */}
                          {cardNickname(c) ? (
                            <Text>{cardNickname(c)}</Text>
                          ) : (
                            <Text color="gray.400">Add name</Text>
                          )}
                        </InlineEdit>
                      </Flex>
                    </Td>
                    <Td textAlign="center">
                      {`${String(c.expMonth || "?").padStart(2, "0")}/${
                        c.expYear || "????"
                      }`}
                    </Td>
                    <Td textAlign="center">
                      <Switch
                        colorScheme="blue"
                        size="lg"
                        isChecked={!!c.isDefault}
                        isDisabled={updating || !!c.isDefault}
                        onChange={() => onSetDefault(c)}
                      />
                    </Td>
                    <Td textAlign="center">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Remove card"
                        isDisabled={updating}
                        onClick={() => onDetach(c)}
                      />
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    No credit cards saved
                  </Td>
                </Tr>
              )}
            </Tbody>
            </Table>
          </Box>
        </VStack>
      </Box>

      <Box
        bg="gray.50"
        borderRadius="12px"
        px={{base: 4, md: 6}}
        py={{base: 4, md: 6}}
        mb={4}
      >
        <VStack spacing={4} alignItems="stretch">
          <Heading size="sm" color="gray.800">
            Add a new credit card
          </Heading>
          <Box w="full">
            <AddCC
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={setErrorMessage}
              onCardAdded={loadCards}
              dashState={state}
              patient={patient}
            />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}
